import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import { ClipboardCopyIcon, CheckIcon, LinkIcon } from '@heroicons/react/outline'
import classNames from 'classnames'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import { hasAdminRole } from 'lib/auth/dashboardRole'

export const USER_DOCUMENT_TYPES = ['idCard', 'drivingLicense', 'passport']

export const getMissingUserDocumentTypes = user => {
  if (!user) return []
  return USER_DOCUMENT_TYPES.filter(documentType => !user[documentType])
}

const DocumentMagicLink = ({ user, userId, bookingId }) => {
  const { t } = useTranslation()
  const currentUser = useSelector(state => get(state, 'user.user'))
  const isAdmin = hasAdminRole(currentUser)
  const [selectedTypes, setSelectedTypes] = useState([])
  const [magicLink, setMagicLink] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [hasCopied, setHasCopied] = useState(false)

  const missingTypes = useMemo(() => getMissingUserDocumentTypes(user), [user])

  const options = useMemo(() => missingTypes.map(documentType => ({ id: documentType, name: t(`userProfile.form.${documentType}`) })), [missingTypes, t])

  const resolvedUserId = userId || user?._id || user?.id

  const handleGenerate = async () => {
    if (!selectedTypes.length) {
      notify.error(t('documentMagicLink.selectAtLeastOne'))
      return
    }
    if (!resolvedUserId) return

    setIsGenerating(true)
    try {
      const payload = { documentTypes: selectedTypes }
      if (bookingId) payload.bookingId = bookingId

      const response = await apiPost(`user-documents/magic-link/${resolvedUserId}`, payload)
      if (response?.success && response?.data?.magicLink) {
        setMagicLink(response.data.magicLink)
        setExpiresAt(response.data.expiresAt || '')
        setHasCopied(false)
      } else {
        notify.error(response?.message || t('documentMagicLink.generateFailed'))
      }
    } catch (error) {
      notify.error(t('documentMagicLink.generateFailed'))
      console.error('Magic link generation error:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = async () => {
    if (!magicLink) return
    try {
      await navigator.clipboard.writeText(magicLink)
      setHasCopied(true)
      notify.success(t('documentMagicLink.copied'))
    } catch (error) {
      notify.error(t('documentMagicLink.copyFailed'))
    }
  }

  const handleReset = () => {
    setMagicLink('')
    setExpiresAt('')
    setSelectedTypes([])
    setHasCopied(false)
  }

  if (!isAdmin || !missingTypes.length || !resolvedUserId) return null

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-start gap-2">
        <LinkIcon className="mt-0.5 h-5 w-5 shrink-0 text-dark-600" />
        <div>
          <h4 className="text-sm font-medium text-gray-900">{t('documentMagicLink.sectionTitle')}</h4>
          <p className="mt-0.5 text-xs text-gray-500">{t('documentMagicLink.sectionDescription')}</p>
        </div>
      </div>

      {magicLink ? (
        <div className="mt-3 space-y-2">
          <p className="break-all rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800" dir="ltr">
            {magicLink}
          </p>
          {expiresAt && <p className="text-xs text-gray-500">{t('documentMagicLink.expiresOn', { date: new Date(expiresAt).toLocaleString() })}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-md bg-dark-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-dark-700"
            >
              {hasCopied ? <CheckIcon className="h-4 w-4" /> : <ClipboardCopyIcon className="h-4 w-4" />}
              {t('documentMagicLink.copy')}
            </button>
            <a
              href={magicLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('documentMagicLink.openLink')}
            </a>
            <button type="button" onClick={handleReset} className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100">
              {t('documentMagicLink.generateAnother')}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <Selection multiple className="w-full" title={t('documentMagicLink.selectPlaceholder')} list={options} value={selectedTypes} onChange={setSelectedTypes} />
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || !selectedTypes.length}
            className={classNames(
              'inline-flex items-center gap-1 rounded-md bg-dark-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-dark-700',
              (isGenerating || !selectedTypes.length) && 'cursor-not-allowed opacity-50'
            )}
          >
            <LinkIcon className="h-4 w-4" />
            {isGenerating ? t('documentMagicLink.generating') : t('documentMagicLink.generateAction')}
          </button>
        </div>
      )}
    </div>
  )
}

export default DocumentMagicLink
